import { useState } from 'react';
import { Pencil, Eraser, ChevronDown, Grid3x3 } from 'lucide-react';
import { useStudioStore } from '@/store/useStudioStore';

interface Note {
  pitch: number;
  start: number;
  length: number;
  velocity: number;
}

export function PianoRoll() {
  const selectedChannelId = useStudioStore((state) => state.selectedChannelId);
  const channels = useStudioStore((state) => state.channels);
  const selectedChannel = channels.findIndex(ch => ch.id === selectedChannelId);
  const [notes, setNotes] = useState<Note[]>([
    { pitch: 60, start: 0, length: 4, velocity: 100 },
    { pitch: 64, start: 4, length: 4, velocity: 85 },
    { pitch: 67, start: 8, length: 4, velocity: 90 },
    { pitch: 72, start: 12, length: 4, velocity: 95 },
  ]);
  
  const [tool, setTool] = useState<'draw' | 'erase'>('draw');
  const [zoom, setZoom] = useState(1);
  
  const octaves = 7;
  const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const totalNotes = octaves * 12;
  const gridWidth = 64; // beats
  
  const isBlackKey = (note: number) => {
    const noteInOctave = note % 12;
    return [1, 3, 6, 8, 10].includes(noteInOctave);
  };
  
  const getNoteLabel = (pitch: number) => {
    const octave = Math.floor(pitch / 12);
    const note = pitch % 12;
    return `${noteNames[note]}${octave}`;
  };

  const handleGridClick = (pitch: number, beat: number) => {
    if (tool === 'draw') {
      const existingNote = notes.findIndex(
        n => n.pitch === pitch && beat >= n.start && beat < n.start + n.length
      );
      
      if (existingNote === -1) {
        setNotes([...notes, { pitch, start: beat, length: 1, velocity: 100 }]);
      }
    } else if (tool === 'erase') {
      setNotes(notes.filter(
        n => !(n.pitch === pitch && beat >= n.start && beat < n.start + n.length)
      ));
    }
  };

  return (
    <div className="flex-1 bg-[#2a2a2a] border-t border-[#3a3a3a] overflow-hidden flex flex-col">
      {/* Toolbar */}
      <div className="h-8 bg-[#1f1f1f] flex items-center px-3 border-b border-[#3a3a3a] justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs">PIANO ROLL - Channel {selectedChannel + 1}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setTool('draw')}
              className={`p-1.5 rounded ${
                tool === 'draw' ? 'bg-[#ff6b00]' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
              }`}
              title="Draw"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={() => setTool('erase')}
              className={`p-1.5 rounded ${
                tool === 'erase' ? 'bg-[#ff6b00]' : 'bg-[#3a3a3a] hover:bg-[#4a4a4a]'
              }`}
              title="Erase"
            >
              <Eraser size={14} />
            </button>
          </div>
          <button className="text-[10px] px-2 py-0.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded flex items-center gap-1">
            <Grid3x3 size={10} />
            Snap: 1/4
            <ChevronDown size={10} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] opacity-50">Zoom</span>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-16"
          />
        </div>
      </div>

      {/* Piano Roll Grid */}
      <div className="flex-1 flex overflow-hidden">
        {/* Piano Keys */}
        <div className="w-16 bg-[#1f1f1f] border-r border-[#3a3a3a] flex flex-col-reverse overflow-hidden">
          {Array.from({ length: totalNotes }, (_, i) => {
            const pitch = i;
            const isBlack = isBlackKey(pitch);
            const isC = pitch % 12 === 0;
            
            return (
              <div
                key={i}
                className={`h-4 border-t border-[#3a3a3a] flex items-center justify-center text-[9px] ${
                  isBlack ? 'bg-[#2a2a2a]' : 'bg-[#1f1f1f]'
                } ${isC ? 'border-l-2 border-l-[#ff6b00]' : ''}`}
              >
                {isC && <span className="opacity-70">{getNoteLabel(pitch)}</span>}
              </div>
            );
          })}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto">
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
            <div className="relative">
              {/* Background grid */}
              <div className="flex flex-col-reverse">
                {Array.from({ length: totalNotes }, (_, i) => {
                  const isBlack = isBlackKey(i);
                  const isC = i % 12 === 0;
                  
                  return (
                    <div key={i} className="flex h-4 border-t border-[#3a3a3a]">
                      {Array.from({ length: gridWidth }, (_, beatIndex) => (
                        <div
                          key={beatIndex}
                          onClick={() => handleGridClick(i, beatIndex)}
                          className={`w-8 border-l cursor-crosshair ${
                            beatIndex % 4 === 0 ? 'border-[#4a4a4a]' : 'border-[#2a2a2a]'
                          } ${isBlack ? 'bg-[#252525]' : 'bg-[#2a2a2a]'} ${
                            isC ? 'border-t-[#ff6b00]' : ''
                          } hover:bg-[#3a3a3a] transition-colors`}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Notes */}
              <div className="absolute inset-0 pointer-events-none">
                {notes.map((note, index) => (
                  <div
                    key={index}
                    className="absolute bg-[#ff6b00] border border-[#ff8b30] rounded pointer-events-auto cursor-move"
                    style={{
                      left: `${note.start * 32}px`,
                      bottom: `${note.pitch * 16}px`,
                      width: `${note.length * 32}px`,
                      height: '16px',
                      opacity: note.velocity / 100,
                    }}
                  >
                    <div className="text-[8px] px-1 text-white/70 truncate">
                      {getNoteLabel(note.pitch)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Beat markers */}
              <div className="absolute top-0 left-0 right-0 h-6 bg-[#1f1f1f] border-b border-[#3a3a3a] flex pointer-events-none">
                {Array.from({ length: gridWidth / 4 }, (_, i) => (
                  <div key={i} className="w-32 border-l border-[#4a4a4a] flex items-center justify-center text-[10px] opacity-50">
                    {i + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom info */}
      <div className="h-6 bg-[#1f1f1f] border-t border-[#3a3a3a] px-3 flex items-center justify-between text-[10px] opacity-70">
        <span>Click to add notes • Right click to delete • Drag to move</span>
        <span>Notes: {notes.length}</span>
      </div>
    </div>
  );
}
